/**
 * OptimizedTestCasesModal (Feature C — Smart Test Optimizer)
 *
 * Displays a responsive diff view (original vs. AI-optimised steps) for each
 * selected test case. The writing style is chosen up-front in OptimizeSetupModal
 * so there is no in-modal style selector or regenerate action.
 *
 * The "Apply" button PATCHes the test case via PUT /api/test-cases/:id.
 */

import { useState, useEffect } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import axios from 'axios';
import {
    X, Sparkles, ChevronDown, ChevronRight, CheckCircle2,
    Loader2, AlertCircle, Info,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config/api';

// ── Types ─────────────────────────────────────────────────────────────────────

export type WritingStyle = 'bdd' | 'tdd' | 'procedural';

interface IOriginalStep {
    action: string;
    expectedResult: string;
    status?: string;
}

interface IOptimizedStep {
    action: string;
    expectedResult: string;
    status: 'PENDING';
}

export interface IOptimizedTestCase {
    originalId: string;
    title: string;
    originalSteps: IOriginalStep[];
    optimizedSteps: IOptimizedStep[];
    duplicatesRemoved: number;
    edgeCases: string[];
    rationale: string;
}

interface ITestCaseLookup {
    title: string;
    type: string;
    description?: string;
    suite?: string;
    preconditions?: string;
    // v2 fields — included so applyTestCase doesn't wipe them on PUT
    severity?:  string;
    caseType?:  string;
    tags?:      string[];
    folderId?:  string;
}

interface OptimizedTestCasesModalProps {
    isOpen: boolean;
    /** Cases returned by the API. Empty while isLoading. */
    optimizedCases: IOptimizedTestCase[];
    /** True while the parent is awaiting the API response. */
    isLoading: boolean;
    /** Writing style chosen in OptimizeSetupModal. Accepts both prop-name spellings. */
    writingStyle?: WritingStyle;
    /** @deprecated Use writingStyle */
    style?: WritingStyle;
    onClose: () => void;
    onApplied: () => void;
    /** Map of originalId → original test case fields needed for the full PUT body. */
    testCasesLookup: Map<string, ITestCaseLookup>;
}

// ── Style config ──────────────────────────────────────────────────────────────

const STYLE_CONFIG: Record<WritingStyle, { label: string; columnLabel: string }> = {
    bdd: { label: 'BDD', columnLabel: 'Optimised (BDD)' },
    tdd: { label: 'TDD', columnLabel: 'Optimised (TDD)' },
    procedural: { label: 'Procedural', columnLabel: 'Optimised (Procedural)' },
};

// ── Step prefix badge (style-aware) ───────────────────────────────────────────

function StepBadge({ action, style }: { action: string; style: WritingStyle }) {
    const lower = action.toLowerCase();

    if (style === 'bdd') {
        if (lower.startsWith('given')) {
            return (
                <span className="inline-block shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-100 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700 mr-1.5">
                    Given
                </span>
            );
        }
        if (lower.startsWith('when')) {
            return (
                <span className="inline-block shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-100 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700 mr-1.5">
                    When
                </span>
            );
        }
        if (lower.startsWith('then')) {
            return (
                <span className="inline-block shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-100 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700 mr-1.5">
                    Then
                </span>
            );
        }
        return null;
    }

    if (style === 'tdd') {
        if (lower.startsWith('setup')) {
            return (
                <span className="inline-block shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-sky-100 dark:bg-sky-950/50 text-sky-700 dark:text-sky-300 border border-sky-200 dark:border-sky-700 mr-1.5">
                    Setup
                </span>
            );
        }
        if (lower.startsWith('execute')) {
            return (
                <span className="inline-block shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-orange-100 dark:bg-orange-950/50 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-700 mr-1.5">
                    Execute
                </span>
            );
        }
        if (lower.startsWith('assert')) {
            return (
                <span className="inline-block shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-teal-100 dark:bg-teal-950/50 text-teal-700 dark:text-teal-300 border border-teal-200 dark:border-teal-700 mr-1.5">
                    Assert
                </span>
            );
        }
        return null;
    }

    // Procedural — numbered steps are self-descriptive
    return null;
}

// ── Loading skeleton ───────────────────────────────────────────────────────────

function LoadingSkeleton() {
    return (
        <div className="flex flex-col items-center justify-center gap-4 py-20 text-slate-400 dark:text-slate-500">
            <div className="relative flex items-center justify-center w-14 h-14 rounded-2xl bg-violet-100 dark:bg-violet-950/30">
                <Loader2 size={26} className="animate-spin text-violet-500 dark:text-violet-400" />
            </div>
            <div className="text-center">
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                    AI is optimising your test cases…
                </p>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                    This may take a few seconds.
                </p>
            </div>
            {/* Animated placeholder cards */}
            <div className="w-full max-w-2xl mt-2 flex flex-col gap-3">
                {(['sk-a', 'sk-b'] as const).map(k => (
                    <div
                        key={k}
                        className="h-24 rounded-xl border border-slate-200 dark:border-gh-border-dark bg-slate-100 dark:bg-slate-800/50 animate-pulse"
                    />
                ))}
            </div>
        </div>
    );
}

// ── Step list ─────────────────────────────────────────────────────────────────

function StepList({
    steps,
    style,
    variant,
}: {
    steps: Array<{ action: string; expectedResult: string }>;
    style: WritingStyle;
    variant: 'original' | 'optimised';
}) {
    if (steps.length === 0) {
        return (
            <p className="text-xs text-slate-400 dark:text-slate-500 italic">
                {variant === 'original' ? 'No steps defined' : 'No optimised steps'}
            </p>
        );
    }

    const isOptimised = variant === 'optimised';

    return (
        <ol className="flex flex-col gap-2">
            {steps.map((step, i) => (
                <li
                    key={step.action.slice(0, 40) || String(i)}
                    className={[
                        'text-xs rounded-lg px-3 py-2.5 border break-words',
                        isOptimised
                            ? 'bg-violet-50 dark:bg-violet-950/20 border-violet-200 dark:border-violet-800 text-slate-700 dark:text-slate-300'
                            : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400',
                    ].join(' ')}
                >
                    <div className="flex items-start gap-1.5 flex-wrap">
                        {isOptimised && <StepBadge action={step.action} style={style} />}
                        {!isOptimised && (
                            <span className="font-semibold text-slate-500 dark:text-slate-400 shrink-0">
                                {i + 1}.
                            </span>
                        )}
                        <span className="whitespace-pre-wrap break-words leading-relaxed">{step.action}</span>
                    </div>
                    {step.expectedResult && (
                        <p className="mt-1.5 pl-1 text-slate-400 dark:text-slate-500 italic leading-relaxed whitespace-pre-wrap break-words">
                            → {step.expectedResult}
                        </p>
                    )}
                </li>
            ))}
        </ol>
    );
}

// ── Single test case panel ─────────────────────────────────────────────────────

interface TestCasePanelProps {
    tc: IOptimizedTestCase;
    style: WritingStyle;
    isApplied: boolean;
    isApplying: boolean;
    onApply: (id: string) => void;
}

function TestCasePanel({ tc, style, isApplied, isApplying, onApply }: TestCasePanelProps) {
    const [expanded, setExpanded] = useState(true);
    const columnLabel = STYLE_CONFIG[style].columnLabel;

    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 overflow-hidden shadow-sm">

            {/* Collapsible header */}
            <button
                type="button"
                data-testid={`optimizer-panel-toggle-${tc.originalId}`}
                aria-expanded={expanded}
                onClick={() => setExpanded(v => !v)}
                className="w-full flex items-center gap-3 px-5 py-3.5 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700/60 transition-colors text-left border-b border-slate-200 dark:border-slate-700"
            >
                {expanded
                    ? <ChevronDown size={15} className="shrink-0 text-slate-400" />
                    : <ChevronRight size={15} className="shrink-0 text-slate-400" />}
                <span className="flex-1 text-sm font-semibold text-slate-800 dark:text-slate-200 break-words">
                    {tc.title}
                </span>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                    {tc.duplicatesRemoved > 0 && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                            -{tc.duplicatesRemoved} dup{tc.duplicatesRemoved !== 1 ? 's' : ''}
                        </span>
                    )}
                    {isApplied && (
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                            <CheckCircle2 size={10} />
                            Applied
                        </span>
                    )}
                </div>
            </button>

            {expanded && (
                <div className="px-5 py-5 flex flex-col gap-5">

                    {/* Rationale */}
                    {tc.rationale && (
                        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-violet-50 dark:bg-violet-950/20 border border-violet-200 dark:border-violet-800 text-xs text-violet-700 dark:text-violet-300 leading-relaxed">
                            <Info size={13} className="shrink-0 mt-0.5" />
                            <span className="whitespace-pre-wrap break-words">{tc.rationale}</span>
                        </div>
                    )}

                    {/* Responsive diff grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                        {/* Left — Original */}
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                    Original
                                </p>
                                <span className="text-[10px] text-slate-400 dark:text-slate-500">
                                    {tc.originalSteps.length} step{tc.originalSteps.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                            <StepList
                                steps={tc.originalSteps}
                                style={style}
                                variant="original"
                            />
                        </div>

                        {/* Right — Optimised */}
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                                <p className="text-xs font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wider">
                                    {columnLabel}
                                </p>
                                <span className="text-[10px] text-slate-400 dark:text-slate-500">
                                    {tc.optimizedSteps.length} step{tc.optimizedSteps.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                            <StepList
                                steps={tc.optimizedSteps}
                                style={style}
                                variant="optimised"
                            />
                        </div>
                    </div>

                    {/* Edge cases */}
                    {tc.edgeCases.length > 0 && (
                        <div className="flex flex-col gap-2 pt-1 border-t border-slate-100 dark:border-slate-700">
                            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                Suggested Edge Cases
                            </p>
                            <ul className="flex flex-col gap-1.5">
                                {tc.edgeCases.map((ec) => (
                                    <li key={ec} className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-400">
                                        <span className="shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 dark:bg-amber-500" />
                                        <span className="whitespace-pre-wrap break-words leading-relaxed">{ec}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Apply button */}
                    <div className="flex justify-end pt-1">
                        <button
                            type="button"
                            data-testid={`optimizer-apply-${tc.originalId}`}
                            onClick={() => onApply(tc.originalId)}
                            disabled={isApplied || isApplying || tc.optimizedSteps.length === 0}
                            className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 active:bg-violet-800 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold transition-colors shadow-sm"
                        >
                            {isApplying
                                ? <><Loader2 size={12} className="animate-spin" /> Applying…</>
                                : isApplied
                                    ? <><CheckCircle2 size={12} /> Applied</>
                                    : <><Sparkles size={12} /> Apply Optimisation</>}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Main modal ─────────────────────────────────────────────────────────────────

export function OptimizedTestCasesModal({
    isOpen,
    optimizedCases,
    isLoading,
    writingStyle,
    style: styleProp,
    onClose,
    onApplied,
    testCasesLookup,
}: OptimizedTestCasesModalProps) {
    // Accept either prop name; fall back to 'bdd' so the component never crashes
    // even if neither is supplied (e.g. during a hot-reload before state settles).
    const style: WritingStyle = writingStyle ?? styleProp ?? 'bdd';

    const { token } = useAuth();
    const [applyingId, setApplyingId] = useState<string | null>(null);
    const [appliedIds, setAppliedIds] = useState<Set<string>>(new Set());
    const [applyError, setApplyError] = useState<string | null>(null);

    // Reset per-session state whenever a new batch of optimized cases arrives.
    useEffect(() => {
        setAppliedIds(new Set());
        setApplyError(null);
    }, [optimizedCases]);

    /** Shared PUT logic — builds the full body required by the backend endpoint. */
    async function applyTestCase(id: string): Promise<void> {
        const tc = optimizedCases.find(c => c.originalId === id);
        if (!tc) throw new Error('Test case not found');

        const original = testCasesLookup.get(id);
        if (!original) throw new Error('Original test case data not available — please refresh and try again');

        const body: Record<string, unknown> = {
            title: original.title,
            type:  original.type,
            steps: tc.optimizedSteps,
        };
        if (original.description)  body.description  = original.description;
        if (original.suite)        body.suite        = original.suite;
        if (original.preconditions) body.preconditions = original.preconditions;
        // Preserve v2 classification fields so they are not wiped by a partial PUT
        if (original.severity)  body.severity  = original.severity;
        if (original.caseType)  body.caseType  = original.caseType;
        if (original.tags?.length) body.tags   = original.tags;
        if (original.folderId)  body.folderId  = original.folderId;

        await axios.put(
            `${API_BASE_URL}/api/test-cases/${id}`,
            body,
            { headers: { Authorization: `Bearer ${token}` } },
        );
    }

    async function handleApply(id: string) {
        setApplyError(null);
        setApplyingId(id);
        try {
            await applyTestCase(id);
            const newApplied = new Set([...appliedIds, id]);
            setAppliedIds(newApplied);
            onApplied();
        } catch (err: unknown) {
            const message = axios.isAxiosError(err)
                ? err.response?.data?.error ?? err.message
                : 'Failed to apply optimisation';
            setApplyError(message);
        } finally {
            setApplyingId(null);
        }
    }

    async function handleApplyAll() {
        setApplyError(null);
        const pending = optimizedCases.filter(
            tc => !appliedIds.has(tc.originalId) && tc.optimizedSteps.length > 0,
        );
        const newlyApplied: string[] = [];
        for (const tc of pending) {
            setApplyingId(tc.originalId);
            try {
                await applyTestCase(tc.originalId);
                newlyApplied.push(tc.originalId);
            } catch (err: unknown) {
                const message = axios.isAxiosError(err)
                    ? err.response?.data?.error ?? err.message
                    : `Failed to apply "${tc.title}"`;
                setApplyError(message);
                break;
            }
        }
        setApplyingId(null);
        if (newlyApplied.length > 0) {
            const newApplied = new Set([...appliedIds, ...newlyApplied]);
            setAppliedIds(newApplied);
            onApplied();
            onClose();
        }
    }

    const pendingCount = optimizedCases.filter(
        tc => !appliedIds.has(tc.originalId) && tc.optimizedSteps.length > 0,
    ).length;

    // Also block close/actions while the parent's API call is still in flight.
    const isBusy = !!applyingId || isLoading;
    const styleLabel = STYLE_CONFIG[style].label;

    return (
        <Transition show={isOpen}>
            <Dialog onClose={onClose} className="relative z-50">

                {/* Backdrop */}
                <TransitionChild
                    enter="transition-opacity duration-200 ease-out"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition-opacity duration-150 ease-in"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-slate-900/60 dark:bg-black/70" aria-hidden="true" />
                </TransitionChild>

                {/* Panel */}
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <TransitionChild
                        enter="transition-all duration-200 ease-out"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="transition-all duration-150 ease-in"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <DialogPanel
                            data-testid="optimizer-modal"
                            className="flex flex-col w-full max-w-6xl max-h-[90vh] overflow-hidden rounded-2xl bg-white dark:bg-gh-bg-dark border border-slate-200 dark:border-gh-border-dark shadow-2xl"
                        >

                            {/* Header — shrink-0 */}
                            <div className="shrink-0 flex items-center justify-between gap-4 px-6 py-4 border-b border-slate-200 dark:border-gh-border-dark">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-violet-100 dark:bg-violet-950/40 text-violet-600 dark:text-violet-400 shrink-0">
                                        <Sparkles size={18} />
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                            AI Test Optimizer
                                            {!isLoading && optimizedCases.length > 0 && (
                                                <span className="ml-1.5 font-normal text-slate-500 dark:text-slate-400">
                                                    — {optimizedCases.length} case{optimizedCases.length !== 1 ? 's' : ''}
                                                </span>
                                            )}
                                        </h3>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                            Style:{' '}
                                            <span className="font-medium text-violet-600 dark:text-violet-400">
                                                {styleLabel}
                                            </span>
                                            {' '}· Review and apply selectively.
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={onClose}
                                    aria-label="Close"
                                    disabled={isBusy}
                                    className="flex items-center justify-center w-8 h-8 rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors shrink-0 disabled:opacity-40"
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            {/* Error banner — shrink-0 */}
                            {applyError && (
                                <div className="shrink-0 mx-6 mt-4 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-400">
                                    <AlertCircle size={13} className="shrink-0 mt-0.5" />
                                    <span className="whitespace-pre-wrap break-words">{applyError}</span>
                                </div>
                            )}

                            {/* Scrollable content area — flex-1 */}
                            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-8">
                                {isLoading ? (
                                    <LoadingSkeleton />
                                ) : (
                                    optimizedCases.map(tc => (
                                        <TestCasePanel
                                            key={tc.originalId}
                                            tc={tc}
                                            style={style}
                                            isApplied={appliedIds.has(tc.originalId)}
                                            isApplying={applyingId === tc.originalId}
                                            onApply={handleApply}
                                        />
                                    ))
                                )}
                            </div>

                            {/* Footer — shrink-0 */}
                            <div className="shrink-0 flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 dark:border-gh-border-dark bg-slate-50 dark:bg-gh-bg-subtle-dark">
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                    {isLoading
                                        ? 'Generating…'
                                        : `${appliedIds.size} of ${optimizedCases.length} applied`}
                                </p>
                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        data-testid="optimizer-cancel-button"
                                        onClick={onClose}
                                        disabled={isBusy}
                                        className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-40"
                                    >
                                        {appliedIds.size > 0 ? 'Done' : 'Cancel'}
                                    </button>
                                    {!isLoading && pendingCount > 1 && (
                                        <button
                                            type="button"
                                            data-testid="optimizer-apply-all-button"
                                            onClick={handleApplyAll}
                                            disabled={isBusy}
                                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 active:bg-violet-800 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors shadow-sm"
                                        >
                                            {isBusy
                                                ? <><Loader2 size={14} className="animate-spin" /> Applying…</>
                                                : <><Sparkles size={14} /> Apply All ({pendingCount})</>}
                                        </button>
                                    )}
                                </div>
                            </div>

                        </DialogPanel>
                    </TransitionChild>
                </div>

            </Dialog>
        </Transition>
    );
}
